import ReactGA from "react-ga4";
import { getCampaignData } from "./campaignTracker";

const TRACKING_ID = "G-RWYS34VRNL"; // G-XXXXXXXXXX

let isGAInitialized = false;
export const initGA = () => {
  if (isGAInitialized) return;
  if (TRACKING_ID) {
    const isDev = process.env.NODE_ENV !== "production";
    ReactGA.initialize(TRACKING_ID, {
      testMode: false,
      gaOptions: {
        debug_mode: isDev,
      },
      gtagOptions: {
        debug_mode: isDev,
      },
    });
    isGAInitialized = true;
  } else {
    console.warn("GA4 ID missing: Analytics disabled.");
  }
};

export const _resetGAForTesting = () => {
  isGAInitialized = false;
};

const ensureGA = () => {
  if (!isGAInitialized) {
    initGA();
  }
};

export const logEvent = (category, action, label) => {
  ensureGA();
  if (TRACKING_ID) {
    ReactGA.event({ category, action, label });
  }
};

// Payload Mappers
const sanitizeItems = (rawItems = [], fallbackItem = {}, defaultCurrency = "EUR") => {
  const itemsList = Array.isArray(rawItems) && rawItems.length > 0
    ? rawItems
    : (fallbackItem.item_id || fallbackItem.item_name || fallbackItem.name)
      ? [fallbackItem]
      : [];

  return itemsList.map((item, idx) => ({
    item_id: String(item.item_id || item.internal_package_id || item.id || `item_${idx + 1}`),
    item_name: String(item.item_name || item.name || "eSIM Package"),
    price: Number(item.price != null ? item.price : 0),
    quantity: Number(item.quantity != null ? item.quantity : 1),
    currency: String(item.currency || defaultCurrency),
    ...(item.item_category ? { item_category: String(item.item_category) } : {}),
  }));
};

const mapPurchasePayload = (data = {}) => {
  const currency = String(data.currency || "EUR");
  const fallbackId = `order_${Date.now()}`;
  const transactionId = String(data.transactionId || data.transaction_id || fallbackId);
  const value = Number(data.value != null ? data.value : (data.total != null ? data.total : (data.price || 0)));
  const price = Number(data.price != null ? data.price : (data.subtotal != null ? data.subtotal : value));
  const campaignData = getCampaignData() || {};

  const items = sanitizeItems(
    data.items,
    {
      item_id: data.item_id,
      item_name: data.item_name,
      price: data.price != null ? data.price : value,
      quantity: data.quantity,
      item_category: data.item_category || (data.purchase_type === "esim_topup" ? "Top-Up" : "eSIM"),
      currency,
    },
    currency
  );

  return {
    transaction_id: transactionId,
    value,
    price,
    currency,
    coupon: data.coupon ? String(data.coupon) : "",
    payment_type: String(data.payment_type || "card"),
    purchase_type: String(data.purchase_type || "esim_purchase"),
    ...(data.user_id || data.userId ? { user_id: String(data.user_id || data.userId) } : {}),
    ...(data.stripe_session_id ? { stripe_session_id: String(data.stripe_session_id) } : {}),
    ...(data.payment_id ? { payment_id: String(data.payment_id) } : {}),
    ...(campaignData.utm_campaign ? { campaign: campaignData.utm_campaign } : {}),
    ...(campaignData.utm_source ? { source: campaignData.utm_source } : {}),
    ...(campaignData.utm_medium ? { medium: campaignData.utm_medium } : {}),
    ...(campaignData.gclid ? { gclid: campaignData.gclid } : {}),
    ...(campaignData.fbclid ? { fbclid: campaignData.fbclid } : {}),
    items,
  };
};

const mapEsimOrderPayload = (data = {}) => {
  return mapPurchasePayload({
    ...data,
    payment_type: data.payment_type || "wallet",
    purchase_type: data.purchase_type || "esim_order",
  });
};

const mapBusinessPayload = (data = {}) => {
  const currency = String(data.currency || "EUR");
  const fallbackId = `tx_${Date.now()}`;
  const transactionId = String(data.transactionId || data.transaction_id || fallbackId);
  const value = Number(data.value != null ? data.value : (data.total != null ? data.total : (data.price || 0)));
  const price = Number(data.price != null ? data.price : value);
  const defaultPaymentType = data.purchase_type === "wallet_topup" ? "card" : "wallet";
  const defaultItemName = data.purchase_type === "wallet_topup"
    ? "Wallet Top-Up"
    : (data.purchase_type === "esim_topup" ? "eSIM Top-Up" : "eSIM Package");
  const defaultCategory = data.purchase_type === "wallet_topup" ? "Wallet" : "eSIM";
  const campaignData = getCampaignData() || {};

  const items = sanitizeItems(
    data.items,
    {
      item_id: data.item_id || "wallet_balance",
      item_name: data.item_name || defaultItemName,
      price,
      quantity: data.quantity || 1,
      item_category: data.item_category || defaultCategory,
      currency,
    },
    currency
  );

  return {
    transaction_id: transactionId,
    value,
    currency,
    coupon: data.coupon ? String(data.coupon) : "",
    payment_type: String(data.payment_type || defaultPaymentType),
    purchase_type: String(data.purchase_type || ""),
    item_id: String(data.item_id || (items[0] && items[0].item_id) || ""),
    item_name: String(data.item_name || (items[0] && items[0].item_name) || defaultItemName),
    item_category: String(data.item_category || (items[0] && items[0].item_category) || ""),
    price,
    quantity: Number(data.quantity || (items[0] && items[0].quantity) || 1),
    ...(data.user_id || data.userId ? { user_id: String(data.user_id || data.userId) } : {}),
    ...(data.stripe_session_id ? { stripe_session_id: String(data.stripe_session_id) } : {}),
    ...(data.payment_id ? { payment_id: String(data.payment_id) } : {}),
    ...(campaignData.utm_campaign ? { campaign: campaignData.utm_campaign } : {}),
    ...(campaignData.utm_source ? { source: campaignData.utm_source } : {}),
    ...(campaignData.utm_medium ? { medium: campaignData.utm_medium } : {}),
    ...(campaignData.gclid ? { gclid: campaignData.gclid } : {}),
    ...(campaignData.fbclid ? { fbclid: campaignData.fbclid } : {}),
    items,
  };
};

export const trackAnalyticsEvent = {
  // setUserId — associates all subsequent sessions, events, and conversions with a specific user
  setUserId: (userId) => {
    ensureGA();
    if (userId && userId !== -1 && userId !== "-1") {
      const idStr = String(userId);
      ReactGA.set({ userId: idStr });
      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("set", "user_properties", { user_id: idStr });
        window.gtag("config", TRACKING_ID, { user_id: idStr });
      }
    } else {
      ReactGA.set({ userId: null });
      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("set", "user_properties", { user_id: null });
        window.gtag("config", TRACKING_ID, { user_id: null });
      }
    }
  },

  // sign_up — triggered when a user registers using a selected method such as email, Apple, Google, or Facebook.
  signUp: (method) => {
    ensureGA();
    const formattedMethod = method ? String(method).toLowerCase() : "email";
    ReactGA.event("sign_up", {
      method: formattedMethod,
    });
  },

  // login — triggered when a user signs in using a selected method such as email, Apple, Google, or Facebook.
  login: (method) => {
    ensureGA();
    const formattedMethod = method ? String(method).toLowerCase() : "email";
    ReactGA.event("login", {
      method: formattedMethod,
    });
  },

  // purchase — triggered when a user buys a package (eSIM or top-up) with card, wallet, paypal, apple pay, etc.
  purchase: (data) => {
    ensureGA();
    ReactGA.event("purchase", mapPurchasePayload(data));
  },

  // wallet_topup — triggered when a user adds funds to their wallet.
  // Tracks both the custom wallet_topup event and the standard ecommerce purchase event so it is recognized as a completed payment.
  walletTopUp: (data) => {
    ensureGA();
    ReactGA.event("wallet_topup", mapBusinessPayload(data));

    const purchasePayload = mapPurchasePayload({
      ...data,
      purchase_type: "wallet_topup",
      payment_type: data.payment_type || "card",
      items: [
        {
          item_id: String(data.item_id || "wallet_balance"),
          item_name: String(data.item_name || "Wallet Top-Up"),
          price: Number(data.value != null ? data.value : (data.price || 0)),
          quantity: 1,
          item_category: "Wallet",
          currency: data.currency || "EUR",
        },
      ],
    });
    ReactGA.event("purchase", purchasePayload);
  },

  // esim_order — triggered when a user buys a new eSIM using wallet balance.
  esimOrder: (data) => {
    ensureGA();
    ReactGA.event("esim_order", mapEsimOrderPayload(data));
  },

  // esim_topup — triggered when a user tops up an existing eSIM using wallet balance.
  esimTopUp: (data) => {
    ensureGA();
    ReactGA.event("esim_topup", mapBusinessPayload(data));
  },

  // screen_view — triggered when a user opens a screen in the app.
  screenView: (screenName) => {
    ensureGA();
    const path = (typeof window !== "undefined" && window.location)
      ? window.location.pathname + window.location.search
      : "";
    ReactGA.send({
      hitType: "pageview",
      page: path,
      title: screenName,
    });
  },
};

